import { IAccount, IAccountInfo } from "@interfaces";
import {
    PLACEHOLDER_PROFILE,
    isPlaceholderDob,
    isPlaceholderPhone,
    isPlaceholderPostCode,
} from "./signupOfferConfig";

/**
 * Turning a brand-new account into a points agent, wherever it was created.
 *
 * Every sign-up path ends here rather than carrying its own copy: the backend's
 * gate (`checkIfUserContainsLeadCustomerDetail`) wants six profile fields before
 * `upgradeToAgent` will answer, and only the checkout paths ever collect them.
 * See .agents/memory/signup-placeholder-profile.md for why the gaps are filled
 * with stand-ins rather than asking for details the sign-up screen never shows.
 */

/** Session key marking an account created this session that still needs upgrading. */
const PENDING_UPGRADE_KEY = "pointsAgentUpgradePending";

const isRealValue = (value?: string | number | null): boolean =>
    typeof value === "number" ? true : !!value && value.trim() !== "";

/**
 * The profile sent immediately before the upgrade.
 *
 * Anything the visitor genuinely gave is kept; only the fields a sign-up screen
 * never asks for fall back to stand-ins. Written as one PUT because
 * `updateUserDetail` replaces the record rather than merging into it, so
 * omitting a field the account already holds would blank it.
 */
export const buildUpgradeProfile = (
    email: string,
    account?: IAccount | null,
    /**
     * Names the sign-up screen collected itself.
     *
     * They take precedence over the account's own: on the path this exists for
     * the account was created moments ago and has none, and where it does have
     * one these were typed by the visitor just now. Supplying them matters more
     * than it looks — the backend mints the agent's branch identity (e.g.
     * `ft-DWF-guesttraveller`) from the name held at that moment and will not
     * change it afterwards, so a stand-in here is permanent.
     */
    signupNames?: { first_name?: string; last_name?: string },
): IAccountInfo => {
    const keep = (value: string | null | undefined, fallback: string) =>
        isRealValue(value) ? (value as string).trim() : fallback;

    return {
        title: keep(account?.title, PLACEHOLDER_PROFILE.title),
        first_name: keep(
            signupNames?.first_name,
            keep(account?.first_name, PLACEHOLDER_PROFILE.first_name),
        ),
        last_name: keep(
            signupNames?.last_name,
            keep(account?.last_name, PLACEHOLDER_PROFILE.last_name),
        ),
        email: keep(email, account?.email ?? ""),
        date_of_birth: keep(account?.date_of_birth, PLACEHOLDER_PROFILE.date_of_birth),
        phone_number: keep(account?.phone_number, PLACEHOLDER_PROFILE.phone_number),
        post_code: keep(account?.post_code, PLACEHOLDER_PROFILE.post_code),
        country_code: keep(account?.country_code, PLACEHOLDER_PROFILE.country_code),
    };
};

/**
 * Whether a Google or Apple sign-in should be treated as a sign-up.
 *
 * Neither provider's login response says whether the account was just created
 * (`IAuthLogin` carries only the two tokens), so the account itself has to be
 * read instead. Names are useless for this — both providers hand the backend a
 * first and last name on the very first sign-in, so a genuinely new account
 * already has them. Date of birth, phone and post code are different: nothing in
 * a social sign-in supplies them, so they are empty until the visitor has been
 * through the traveller form. All three blank (or still holding stand-ins) means
 * this account has never been used to book, which is as close to "new" as the
 * backend lets us get.
 */
export const looksLikeNewSocialAccount = (account?: IAccount | null): boolean => {
    if (!account || account.is_agent_integrated) return false;

    const dobIsOwn = isRealValue(account.date_of_birth) && !isPlaceholderDob(account.date_of_birth);
    const phoneIsOwn =
        isRealValue(account.phone_number) && !isPlaceholderPhone(account.phone_number);
    const postCodeIsOwn =
        isRealValue(account.post_code) && !isPlaceholderPostCode(account.post_code);

    return !dobIsOwn && !phoneIsOwn && !postCodeIsOwn;
};

/**
 * Flag a just-created account so the upgrade runs once its email is verified.
 *
 * Sign-ups that hand off to the app's own verification screen (the `/signup`
 * page from the burger menu) cannot upgrade on the spot — the account is not
 * verified yet, and the visitor leaves the sign-up component entirely. The flag
 * survives that navigation in sessionStorage, and `usePointsAgentUpgrade` picks
 * it up when the account comes back verified.
 */
export const markSignupPendingUpgrade = (email: string) => {
    try {
        sessionStorage.setItem(PENDING_UPGRADE_KEY, email);
    } catch {
        // Private-mode storage failures are not worth failing a sign-up over; the
        // checkout's own upgrade path remains.
    }
};

export const readSignupPendingUpgrade = (): string | null => {
    try {
        return sessionStorage.getItem(PENDING_UPGRADE_KEY);
    } catch {
        return null;
    }
};

export const clearSignupPendingUpgrade = () => {
    try {
        sessionStorage.removeItem(PENDING_UPGRADE_KEY);
    } catch {
        // Nothing to do: a flag that cannot be cleared is re-read against an
        // account that is already an agent, and the upgrade then no-ops.
    }
};

interface IEnsureUpgradeArgs {
    /** Address the account was created with; falls back to the account's own. */
    email: string;
    account?: IAccount | null;
    /** Names from the sign-up screen, for an account the backend has not been told them. */
    signupNames?: { first_name?: string; last_name?: string };
    dispatch: (action: any) => any;
    /** Surfaces a failure to the visitor. Omit where there is no sensible place to show one. */
    onError?: (message: string) => void;
}

/**
 * Write the profile, upgrade, and refresh the flags the points UI reads.
 *
 * Never throws: an account that fails to become an agent can still book, and the
 * traveller form's "Save and Earn Points" is still a route to the upgrade. The
 * API modules are imported lazily so that the pure helpers above can be unit
 * tested without Jest trying to parse axios's ESM (see CLAUDE.md).
 */
export const ensurePointsAgentUpgrade = async ({
    email,
    account,
    signupNames,
    dispatch,
    onError,
}: IEnsureUpgradeArgs): Promise<boolean> => {
    if (account?.is_agent_integrated) return false;

    try {
        const { accountApi } = await import("src/Api/accountApi");
        const { pointsApi } = await import("src/Api/pointsApi");
        const { getMetaData, initializeAccountDetail } = await import("@redux");

        await accountApi.updateUserDetail(buildUpgradeProfile(email, account, signupNames));

        const referral = new URLSearchParams(window.location.search).get("referrer");
        await pointsApi.upgradeToPointsAgent(referral || undefined);

        // Refresh both halves of what the points UI reads: getMetaData carries the
        // agent-type flags, initializeAccountDetail the is_agent_integrated flag
        // that every canEarnPoints check tests.
        await dispatch(getMetaData());
        await dispatch(initializeAccountDetail());
        return true;
    } catch (error: any) {
        console.error("Could not enable points on the new account:", error);
        onError?.(
            error?.response?.data?.title ||
                "Your account is ready, but we couldn't switch on points for this booking",
        );
        return false;
    }
};
